/* Iter110 — Mandatory Business Snapshot interview conducted by David.
   Renders as a conversational form INSIDE the David chat panel. Every
   answer is saved to Supabase immediately (incremental save — founder
   can close the browser mid-way and resume). Field-typed input widget
   below each question keeps data quality high without breaking the
   chat vibe. */

import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, PencilSimple } from "@phosphor-icons/react";

const C = { teal: "#0A8B85", tealSoft: "#7BD3CE", orange: "#F59E0B", panel: "#0A2826" };
const AVATAR_SRC = "/david-avatar.png";

export default function SnapshotInterview({ state, onStateChange, onCompleted }) {
  // `state` is the payload from GET /api/david/snapshot — the parent (David.jsx)
  // owns the fetch so open/close doesn't re-trigger the coach intro every time.
  const field = state?.next_field;
  const progress = state?.progress || { answered: 0, total: 0, percent: 0 };
  const [answer, setAnswer] = useState(initialAnswer(field));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setAnswer(initialAnswer(field)); }, [field?.key]);

  const submit = async () => {
    if (!field || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/david/snapshot/answer", {
        field_key: field.key, value: answer,
      });
      onStateChange?.(data);
      if (data.completed) {
        onCompleted?.(data);
        toast.success("Snapshot complete — David is unlocked!", {
          duration: 10000,
          action: { label: "Build your pitch deck →", onClick: () => { window.location.href = "/pitch/new"; } },
          cancel: { label: "Financial model →", onClick: () => { window.location.href = "/fm/new"; } },
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save — try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (state?.completed) return null;

  return (
    <div data-testid="david-snapshot-interview" className="space-y-3">
      {/* Coach intro — shown only on the first field */}
      {progress.answered === 0 && state?.coach_intro && (
        <div className="flex items-start gap-2" data-testid="david-coach-intro">
          <img src={AVATAR_SRC} alt="David"
            className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5"
            style={{ border: `1.5px solid ${C.teal}` }} />
          <div className="max-w-[82%] rounded-2xl rounded-bl-[4px] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
            style={{ backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(123,211,206,0.12)", color: "rgba(255,255,255,0.92)" }}>
            {state.coach_intro}
          </div>
        </div>
      )}

      {/* Progress bar */}
      <ProgressBar progress={progress} />

      {/* Current question — bubble */}
      {field && (
        <div className="flex items-start gap-2">
          <img src={AVATAR_SRC} alt="David"
            className="w-7 h-7 rounded-full object-cover shrink-0 mt-0.5"
            style={{ border: `1.5px solid ${C.teal}` }} />
          <div className="max-w-[82%] rounded-2xl rounded-bl-[4px] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
            style={{ backgroundColor: "rgba(255,255,255,0.05)", border: "1px solid rgba(123,211,206,0.12)", color: "rgba(255,255,255,0.92)" }}
            data-testid={`david-snapshot-question-${field.key}`}>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold mb-1"
              style={{ color: C.orange }}>
              Step {progress.answered + 1} of {progress.total} · {field.label}
            </div>
            {field.question}
            {field.hint && <div className="text-[11px] text-white/40 italic mt-1.5">{field.hint}</div>}
          </div>
        </div>
      )}

      {/* Input widget for the current field */}
      {field && (
        <div className="pl-9 pr-1 pb-1">
          <FieldInput field={field} value={answer} onChange={setAnswer}
            onEnter={submit} disabled={submitting} />
          <div className="flex justify-end mt-2">
            <button onClick={submit} disabled={submitting || !isFilled(field, answer)}
              data-testid="david-snapshot-submit"
              className="px-4 py-1.5 rounded-md text-xs font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: C.teal, color: "#fff" }}>
              {submitting ? "Saving…" : "Continue →"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


/* Small module-level state check-in for the DavidWidget — call
   getSnapshotState() when the panel opens to decide whether to show
   the interview or the normal chat. */
export async function getSnapshotState() {
  try {
    const { data } = await api.get("/david/snapshot");
    return data;
  } catch (e) {
    const status = e?.response?.status;
    const detail = e?.response?.data?.detail || "";
    // 503 = Supabase not wired at all
    if (status === 503) return { completed: true, missing_supabase: true };
    // 424 = table missing — admin must create it
    if (status === 424 || /business_snapshots/i.test(detail) || /schema cache/i.test(detail)) {
      return {
        completed: false,
        error: detail || "The Business Snapshot table doesn't exist in Supabase yet.",
      };
    }
    return { completed: false, error: detail || "Could not load your snapshot." };
  }
}


export function CompletedBadge({ onReopen }) {
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-md text-[11px]"
      data-testid="david-snapshot-completed-badge"
      style={{ backgroundColor: "rgba(10,139,133,0.14)", border: "1px solid rgba(123,211,206,0.28)" }}>
      <span className="inline-flex items-center gap-1.5 text-white/85 font-semibold">
        <CheckCircle size={12} weight="fill" color={C.tealSoft} /> Snapshot complete
      </span>
      <button onClick={onReopen} data-testid="david-snapshot-reopen"
        className="inline-flex items-center gap-1 text-white/55 hover:text-white text-[10px] uppercase tracking-wider font-bold">
        <PencilSimple size={10} /> Update
      </button>
    </div>
  );
}


/* ---------------- helpers ---------------- */

function initialAnswer(field) {
  if (!field) return "";
  switch (field.type) {
    case "multi_select": return [];
    case "capital_mix":  return { equity: 0, debt: 0, grant: 0, other: 0 };
    default:              return "";
  }
}

function isFilled(field, v) {
  if (field.optional) return true;
  switch (field.type) {
    case "multi_select": return Array.isArray(v) && v.length > 0;
    case "capital_mix":  return Object.values(v || {}).some((x) => Number(x) > 0);
    case "number":
    case "year":
    case "money":        return v !== "" && v !== null && !Number.isNaN(Number(v));
    default:              return typeof v === "string" && v.trim().length > 0;
  }
}


function ProgressBar({ progress }) {
  return (
    <div className="px-1" data-testid="david-snapshot-progress">
      <div className="flex justify-between text-[10px] font-mono-num tabular-nums text-white/55 mb-1">
        <span>{progress.answered} / {progress.total} answered</span>
        <span>{progress.percent}%</span>
      </div>
      <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
        <div className="h-full transition-all" style={{ width: `${progress.percent}%`, backgroundColor: C.orange }} />
      </div>
    </div>
  );
}


function FieldInput({ field, value, onChange, onEnter, disabled }) {
  const base = "w-full px-3 py-2 rounded-md text-sm focus:outline-none";
  const style = {
    backgroundColor: "rgba(0,0,0,0.45)",
    border: "1px solid rgba(255,255,255,0.15)",
    color: "#fff",
  };
  const commonProps = {
    disabled,
    "data-testid": `david-snapshot-input-${field.key}`,
    onKeyDown: (e) => {
      if (e.key === "Enter" && field.type !== "longtext" && field.type !== "multi_select" && field.type !== "capital_mix") {
        e.preventDefault();
        onEnter?.();
      }
    },
  };
  switch (field.type) {
    case "longtext":
      return (
        <textarea {...commonProps} rows={3} value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.hint || ""}
          className={base + " resize-none"} style={style} />
      );
    case "select":
      return (
        <select {...commonProps} value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className={base} style={style}>
          <option value="">— pick one —</option>
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      );
    case "multi_select":
      return (
        <div className="grid grid-cols-2 gap-1.5" style={{ maxHeight: 220, overflowY: "auto", padding: 4 }}>
          {field.options.map((o) => {
            const active = Array.isArray(value) && value.includes(o.value);
            return (
              <button key={o.value} type="button" disabled={disabled}
                data-testid={`david-snapshot-opt-${field.key}-${o.value}`}
                onClick={() => {
                  const set = new Set(value || []);
                  if (active) set.delete(o.value);
                  else if (set.size < (field.max_select || 3)) set.add(o.value);
                  onChange([...set]);
                }}
                className="text-left px-2.5 py-1.5 rounded text-[11px] transition-colors"
                style={active ? {
                  backgroundColor: "rgba(10,139,133,0.35)",
                  border: `1px solid ${C.teal}`, color: "#fff",
                } : {
                  backgroundColor: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "rgba(255,255,255,0.7)",
                }}>
                {o.label}
              </button>
            );
          })}
        </div>
      );
    case "capital_mix":
      return <CapitalMixInput value={value} onChange={onChange} disabled={disabled} />;
    case "money":
      return (
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-white/40 font-bold">USD</span>
          <input {...commonProps} type="text" inputMode="decimal"
            value={value === 0 ? "0" : (value ?? "")}
            onChange={(e) => onChange(e.target.value.replace(/[^0-9.-]/g, ""))}
            placeholder={field.hint || "0"}
            className={base + " pl-12"} style={style} />
        </div>
      );
    case "number":
    case "year":
      return (
        <input {...commonProps} type="number" min={field.min} max={field.max}
          value={value ?? ""} onChange={(e) => onChange(e.target.value)}
          placeholder={field.hint || ""}
          className={base} style={style} />
      );
    default: // text, url
      return (
        <input {...commonProps} type="text" value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.hint || ""}
          className={base} style={style} />
      );
  }
}


function CapitalMixInput({ value, onChange, disabled }) {
  const v = value || { equity: 0, debt: 0, grant: 0, other: 0 };
  const total = ["equity", "debt", "grant", "other"].reduce((s, k) => s + (Number(v[k]) || 0), 0);
  const ok = Math.abs(total - 100) <= 0.5;
  const rows = [
    { key: "equity", label: "Equity" },
    { key: "debt",   label: "Debt" },
    { key: "grant",  label: "Grant" },
    { key: "other",  label: "Other" },
  ];
  return (
    <div className="space-y-1.5" data-testid="david-snapshot-capital-mix">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2">
          <label className="text-xs text-white/70 w-14 shrink-0">{r.label}</label>
          <input type="number" min={0} max={100} step={1}
            disabled={disabled}
            data-testid={`david-snapshot-capital-mix-${r.key}`}
            value={v[r.key] ?? 0}
            onChange={(e) => onChange({ ...v, [r.key]: Number(e.target.value || 0) })}
            className="flex-1 px-2.5 py-1.5 rounded text-xs focus:outline-none"
            style={{ backgroundColor: "rgba(0,0,0,0.45)", border: "1px solid rgba(255,255,255,0.15)", color: "#fff" }} />
          <span className="text-xs text-white/40 w-4">%</span>
        </div>
      ))}
      <div className="flex justify-between text-[11px] font-mono-num tabular-nums pt-1"
        style={{ color: ok ? C.tealSoft : C.orange }}>
        <span>Total</span>
        <span>{total}% {ok ? "✓" : "(must be 100%)"}</span>
      </div>
    </div>
  );
}
